/**
 * Presence UI (Collab Phase 10) — live cursors, the toolbar avatar stack, and
 * peer section badges. Renders from the pure presence store (presence.js);
 * sends the local user's cursor/selection/editing/viewport as throttled
 * presence frames. DOM-heavy and manual-tested; the reducer it drives is unit-
 * tested in collab-presence-tests.js.
 *
 * Configured by the shell with accessors it owns (overlay content element,
 * toolbar mount, the collab client's sendPresence/getSelfConnId). Inert until
 * `collab:connected`; fully torn down on `collab:disconnected`.
 */

import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { bridge } from '../editing/bridge.js';
import { getActiveViewportName } from '../ui/viewport-controls.js';
import { showToast } from '../ui/toast.js';
import { createPresenceStore, cursorVisible, createPresenceThrottle } from './presence.js';

let cfg = {
  getContentElement: () => null,
  getPresenceMount: () => null,
  sendPresence: () => {},
  getSelfConnId: () => null
};
let presence = null;
let throttle = null;
let active = false;
let avatarsEl = null;
const cursorEls = new Map(); // connId -> element
let pruneTimer = 0;

// Local presence state (coalesced before send).
let localCursor = null;
let localSelection = null;
let localEditingKey = null;

function now() {
  return Date.now();
}

// ── local send ──
function sendLocalPresence(force = false) {
  if (!active) return;
  if (!force && throttle && !throttle.due(now())) return;
  throttle?.mark(now());
  cfg.sendPresence({
    cursor: localCursor ? { ...localCursor, viewportName: getActiveViewportName() } : null,
    selection: localSelection,
    editingSectionKey: localEditingKey,
    pageId: store.getState()?.activePage || null
  });
}

// ── cursor rendering ──
function ensureCursorEl(peer) {
  let el = cursorEls.get(peer.connId);
  if (el) return el;
  const content = cfg.getContentElement();
  if (!content) return null;
  el = document.createElement('div');
  el.className = 'pb-collab-cursor';
  el.style.setProperty('--pb-collab-color', peer.color);
  el.innerHTML =
    '<svg class="pb-collab-cursor-arrow" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">' +
    '<path d="M2 2 L2 15 L6 11 L9 17 L11 16 L8 10 L14 10 Z"/></svg>' +
    '<span class="pb-collab-cursor-name"></span>';
  content.appendChild(el);
  cursorEls.set(peer.connId, el);
  return el;
}

function renderCursor(peer, localViewport) {
  const visible = cursorVisible(peer, localViewport);
  const existing = cursorEls.get(peer.connId);
  if (!visible) {
    if (existing) existing.style.display = 'none';
    return;
  }
  const el = ensureCursorEl(peer);
  if (!el) return;
  const zoom = bridge.getZoom ? bridge.getZoom() : 1;
  el.style.display = '';
  el.style.transform = `translate(${peer.cursor.x * zoom}px, ${peer.cursor.y * zoom}px)`;
  const nameEl = el.querySelector('.pb-collab-cursor-name');
  if (nameEl) nameEl.textContent = peer.email || 'Guest';
  el.style.setProperty('--pb-collab-color', peer.color);
}

function dropCursor(connId) {
  const el = cursorEls.get(connId);
  if (el) {
    el.remove();
    cursorEls.delete(connId);
  }
}

// ── avatar stack ──
/**
 * CMD-3: the host is an EXPLICIT mount the shell hands over, not a position
 * inferred from the toolbar's markup. The old code queried `.pb-toolbar-left`
 * and inserted before `#pb-auth-chip` — two structural assumptions about a bar
 * that has since been rebuilt, and both would have failed silently (no avatars,
 * no error) rather than loudly.
 */
function ensureAvatars() {
  if (avatarsEl && avatarsEl.isConnected) return avatarsEl;
  const mount = cfg.getPresenceMount?.();
  if (!mount) return null;
  avatarsEl = document.createElement('div');
  avatarsEl.className = 'pb-collab-avatars';
  mount.appendChild(avatarsEl);
  return avatarsEl;
}

function initials(peer) {
  const s = (peer.email || '').trim();
  if (!s) return '?';
  const at = s.indexOf('@');
  return (at > 0 ? s.slice(0, at) : s).slice(0, 2).toUpperCase();
}

function renderAvatars() {
  const el = ensureAvatars();
  if (!el) return;
  const peers = presence ? presence.list() : [];
  el.innerHTML = '';
  for (const peer of peers) {
    const dot = document.createElement('span');
    dot.className = 'pb-collab-avatar';
    dot.style.setProperty('--pb-collab-color', peer.color);
    dot.textContent = initials(peer);
    const vp = peer.cursor?.viewportName;
    dot.title = `${peer.email || 'Guest'}${vp ? ` — viewing ${vp}` : ''}`;
    el.appendChild(dot);
  }
}

// ── section presence badge (lighter than the lock nameplate) ──
function pushSectionPresence() {
  const state = store.getState();
  const rows = [];
  for (const peer of presence ? presence.list() : []) {
    const key = peer.editingSectionKey || (peer.selection && `s:${peer.selection.instanceId}`);
    let instanceId = null;
    if (typeof key === 'string' && key.startsWith('s:')) instanceId = key.slice(2);
    else if (key === 'shared:navigation')
      instanceId = state?.sharedSections?.navigation?.instanceId || null;
    else if (key === 'shared:footer')
      instanceId = state?.sharedSections?.footer?.instanceId || null;
    if (instanceId) rows.push({ instanceId, color: peer.color });
  }
  try {
    bridge.send('set-collab-presence', { rows });
  } catch {
    /* iframe not ready */
  }
}

// ── render everything ──
function renderAll() {
  const localViewport = getActiveViewportName();
  const peers = presence ? presence.list() : [];
  const live = new Set(peers.map((p) => p.connId));
  for (const connId of [...cursorEls.keys()]) if (!live.has(connId)) dropCursor(connId);
  for (const peer of peers) renderCursor(peer, localViewport);
  renderAvatars();
  pushSectionPresence();
}

// ── join/leave toasts (deduped) ──
function toastRoster(prevIds, nextPeers) {
  const nextIds = new Set(nextPeers.map((p) => p.connId));
  for (const peer of nextPeers) {
    if (!prevIds.has(peer.connId)) {
      showToast(`${peer.email || 'A collaborator'} joined`, {
        type: 'info',
        id: `collab-join-${peer.connId}`,
        duration: 3500
      });
    }
  }
  for (const id of prevIds) {
    if (!nextIds.has(id))
      showToast('A collaborator left', { type: 'info', id: `collab-left-${id}`, duration: 3000 });
  }
}

// ── activation ──
function activate() {
  if (active) return;
  active = true;
  presence = createPresenceStore({ now });
  throttle = createPresenceThrottle(80);
  bridge.send('set-collab-active', { active: true });
  if (!pruneTimer) {
    pruneTimer = setInterval(() => {
      presence?.prune();
      renderAll();
    }, 3000);
  }
}

function deactivate() {
  active = false;
  if (pruneTimer) {
    clearInterval(pruneTimer);
    pruneTimer = 0;
  }
  for (const connId of [...cursorEls.keys()]) dropCursor(connId);
  if (avatarsEl) {
    avatarsEl.remove();
    avatarsEl = null;
  }
  try {
    bridge.send('set-collab-active', { active: false });
  } catch {
    /* ignore */
  }
  try {
    bridge.send('set-collab-presence', { rows: [] });
  } catch {
    /* ignore */
  }
  presence = null;
  localCursor = localSelection = localEditingKey = null;
}

// ── wiring ──
export function initCollabUI(accessors = {}) {
  cfg = { ...cfg, ...accessors };

  eventBus.on('collab:connected', activate);
  eventBus.on('collab:disconnected', deactivate);

  eventBus.on('collab:peers-changed', ({ peers } = {}) => {
    if (!active || !presence) return;
    const prevIds = new Set(presence.list().map((p) => p.connId));
    presence.setRoster(peers || []);
    toastRoster(prevIds, presence.list());
    renderAll();
  });

  eventBus.on('collab:presence', (frame) => {
    if (!active || !presence) return;
    presence.applyPresence(frame);
    renderAll();
  });

  // Local pointer from the iframe reporter → local presence cursor.
  eventBus.on('iframe:collab-pointer', (point) => {
    if (!active) return;
    localCursor = point ? { x: point.x, y: point.y } : null;
    sendLocalPresence();
  });

  // Local editing focus → presence (renews the section lock server-side).
  eventBus.on('editing:text-started', ({ instanceId } = {}) => {
    if (!active || !instanceId) return;
    localEditingKey = `s:${instanceId}`;
    sendLocalPresence(true);
  });
  eventBus.on('editing:text-ended', () => {
    if (!active) return;
    localEditingKey = null;
    sendLocalPresence(true);
  });

  // Local selection → presence. Listens to the selection store's derived event
  // (16A0.5) rather than `section:select`, which only the programmatic
  // post-render path ever emitted — plain canvas and sidebar clicks never
  // travelled through it, so peer badges silently went stale on every ordinary
  // click. `primary` is the single section every legacy consumer acts on; when
  // multi-select lands, peers keep seeing one badge, on the lead.
  eventBus.on('selection:changed', ({ primary } = {}) => {
    if (!active) return;
    localSelection = primary ? { instanceId: primary } : null;
    sendLocalPresence(true);
  });

  // Viewport change → re-evaluate cursor visibility + republish.
  eventBus.on('viewport:changed', () => {
    if (!active) return;
    renderAll();
    sendLocalPresence(true);
  });
}
